import { useEffect, useRef, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { ORDER_DOCUMENT_KIND_LABELS_RU, OrderDocumentKind } from '@crm/shared'
import type {
  OrderDetailDto,
  OrderDocumentDto,
  OrderDocumentKind as OrderDocumentKindT,
} from '@crm/shared'
import { uploadOrderDocument, deleteOrderDocument } from '../../../shared/api/orders.api'
import { formatDate } from '../orderFormat'
import { confirm } from '../../../shared/ui/confirmStore'
import { loadXlsx } from '../../../shared/utils/loadXlsx'

// Расширения для атрибута accept и подсказки (синхронно с бэком orders.upload.ts).
const ACCEPT = '.jpg,.jpeg,.png,.webp,.pdf,.xlsx,.xls,.doc,.docx,.ppt,.pptx'
const ALLOWED_HINT = 'jpg, png, webp, pdf, xlsx, doc, docx, ppt, pptx · до 50 МБ'

function extractError(e: unknown): string {
  const err = e as { response?: { data?: { error?: { message?: string } } } }
  return err.response?.data?.error?.message ?? 'Не удалось загрузить файл'
}

function isImageDoc(d: OrderDocumentDto): boolean {
  const ext = ((d.originalName ?? d.fileName).split('.').pop() ?? '').toLowerCase()
  return (
    (d.mimeType ?? '').startsWith('image/') || ['jpg', 'jpeg', 'png', 'webp', 'gif'].includes(ext)
  )
}

function isPdfDoc(d: OrderDocumentDto): boolean {
  const ext = ((d.originalName ?? d.fileName).split('.').pop() ?? '').toLowerCase()
  return (d.mimeType ?? '') === 'application/pdf' || ext === 'pdf'
}

function isExcelDoc(d: OrderDocumentDto): boolean {
  const ext = ((d.originalName ?? d.fileName).split('.').pop() ?? '').toLowerCase()
  const m = d.mimeType ?? ''
  return (
    ext === 'xlsx' ||
    ext === 'xls' ||
    ext === 'csv' ||
    m.includes('spreadsheet') ||
    m.includes('ms-excel')
  )
}

export function DocumentsSection({
  order,
  canMutate,
}: {
  order: OrderDetailDto
  canMutate: boolean
}) {
  const qc = useQueryClient()
  const fileRef = useRef<HTMLInputElement>(null)
  const [kind, setKind] = useState<OrderDocumentKindT>('PAYMENT_PROOF')
  const [error, setError] = useState<string | null>(null)
  const [preview, setPreview] = useState<OrderDocumentDto | null>(null)

  const upload = useMutation({
    mutationFn: (file: File) => uploadOrderDocument(order.id, file, kind),
    onSuccess: () => {
      if (fileRef.current) fileRef.current.value = ''
      void qc.invalidateQueries({ queryKey: ['orders', order.id] })
    },
    onError: (e) => setError(extractError(e)),
  })

  return (
    <section className="rounded-xl border border-gray-200 bg-white p-4">
      <h2 className="mb-3 text-base font-semibold">Документы ({order.documents.length})</h2>

      {canMutate && (
        <div className="mb-1 flex flex-wrap items-center gap-2">
          <select
            className="rounded border border-gray-300 px-2 py-1.5 text-sm"
            value={kind}
            onChange={(e) => setKind(e.target.value as OrderDocumentKindT)}
          >
            {Object.values(OrderDocumentKind).map((k) => (
              <option key={k} value={k}>
                {ORDER_DOCUMENT_KIND_LABELS_RU[k]}
              </option>
            ))}
          </select>
          <input
            ref={fileRef}
            type="file"
            accept={ACCEPT}
            className="text-sm"
            onChange={(e) => {
              setError(null)
              const f = e.target.files?.[0]
              if (f) upload.mutate(f)
            }}
            disabled={upload.isPending}
          />
          {upload.isPending && <span className="text-xs text-gray-500">Загрузка…</span>}
        </div>
      )}
      {canMutate && <p className="mb-3 text-xs text-gray-500">Разрешены: {ALLOWED_HINT}</p>}
      {error && <p className="mb-2 text-sm text-red-600">{error}</p>}

      {order.documents.length === 0 ? (
        <p className="text-sm text-gray-500">Документов нет.</p>
      ) : (
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {order.documents.map((d) => (
            <button
              key={d.id}
              type="button"
              onClick={() => setPreview(d)}
              className="flex items-center gap-3 rounded-lg border border-gray-200 p-2 text-left hover:bg-gray-50"
            >
              <FileGlyph name={d.originalName ?? d.fileName} mimeType={d.mimeType} url={d.url} />
              <div className="min-w-0">
                <div className="truncate text-sm font-medium text-gray-800">
                  {d.originalName ?? d.fileName}
                </div>
                <div className="truncate text-xs text-gray-500">
                  {ORDER_DOCUMENT_KIND_LABELS_RU[d.kind]} · {formatDate(d.createdAt)}
                </div>
              </div>
            </button>
          ))}
        </div>
      )}

      {preview && (
        <DocumentPreviewModal
          doc={preview}
          orderId={order.id}
          canMutate={canMutate}
          onClose={() => setPreview(null)}
        />
      )}
    </section>
  )
}

function DocumentPreviewModal({
  doc,
  orderId,
  canMutate,
  onClose,
}: {
  doc: OrderDocumentDto
  orderId: string
  canMutate: boolean
  onClose: () => void
}) {
  const qc = useQueryClient()
  const name = doc.originalName ?? doc.fileName
  const remove = useMutation({
    mutationFn: () => deleteOrderDocument(doc.id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['orders', orderId] })
      onClose()
    },
  })

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="flex max-h-[88vh] w-full max-w-4xl flex-col overflow-hidden rounded-xl bg-white shadow-xl">
        <div className="flex items-center justify-between gap-3 border-b border-gray-200 px-4 py-3">
          <div className="min-w-0">
            <div className="truncate text-sm font-semibold text-gray-900">{name}</div>
            <div className="truncate text-xs text-gray-500">
              {ORDER_DOCUMENT_KIND_LABELS_RU[doc.kind]} · {formatDate(doc.createdAt)}
            </div>
          </div>
          <button className="shrink-0 text-gray-400 hover:text-gray-600" onClick={onClose}>
            ✕
          </button>
        </div>

        <div className="flex-1 overflow-auto bg-gray-50 p-3">
          {isImageDoc(doc) ? (
            <img src={doc.url} alt={name} className="mx-auto max-h-[64vh] rounded object-contain" />
          ) : isPdfDoc(doc) ? (
            <iframe src={doc.url} title={name} className="h-[64vh] w-full rounded border-0" />
          ) : isExcelDoc(doc) ? (
            <ExcelPreview url={doc.url} />
          ) : (
            <div className="py-16 text-center text-sm text-gray-500">
              Предпросмотр для этого типа файла недоступен — скачайте, чтобы открыть.
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-gray-200 px-4 py-3">
          {canMutate && (
            <button
              className="rounded-lg px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 disabled:opacity-60"
              onClick={() => {
                void confirm({
                  title: 'Удалить документ?',
                  message: 'Файл будет удалён безвозвратно.',
                  danger: true,
                  confirmText: 'Удалить',
                }).then((ok) => ok && remove.mutate())
              }}
              disabled={remove.isPending}
            >
              {remove.isPending ? 'Удаляю…' : 'Удалить'}
            </button>
          )}
          <a
            href={doc.url}
            download={name}
            className="rounded-lg bg-brand-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-700"
          >
            Скачать
          </a>
          <button
            className="rounded-lg bg-gray-100 px-3 py-1.5 text-sm hover:bg-gray-200"
            onClick={onClose}
          >
            Закрыть
          </button>
        </div>
      </div>
    </div>
  )
}

// Предпросмотр Excel: тянем файл, парсим SheetJS'ом и рендерим первый лист как
// обычную таблицу через sheet_to_json (без innerHTML — без риска XSS из файла).
// xlsx грузим динамически через общий хелпер loadXlsx — тяжёлая либа лежит в
// отдельном чанке (общем для всех мест работы с Excel) и в основной бандл не попадает.
const PREVIEW_ROWS = 200
const PREVIEW_COLS = 40

function ExcelPreview({ url }: { url: string }) {
  const [rows, setRows] = useState<string[][] | null>(null)
  const [truncated, setTruncated] = useState(false)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    let cancelled = false
    setRows(null)
    setFailed(false)
    setTruncated(false)
    ;(async () => {
      try {
        const res = await fetch(url)
        if (!res.ok) throw new Error('fetch failed')
        const buf = await res.arrayBuffer()
        const XLSX = await loadXlsx()
        const wb = XLSX.read(buf, { type: 'array' })
        const sheet = wb.Sheets[wb.SheetNames[0]!]
        if (!sheet) throw new Error('no sheet')
        const data = XLSX.utils.sheet_to_json<unknown[]>(sheet, {
          header: 1,
          blankrows: false,
          defval: '',
        })
        const limited = data
          .slice(0, PREVIEW_ROWS)
          .map((r) => (r as unknown[]).slice(0, PREVIEW_COLS).map((c) => String(c ?? '')))
        if (!cancelled) {
          setRows(limited)
          setTruncated(data.length > PREVIEW_ROWS)
        }
      } catch {
        if (!cancelled) setFailed(true)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [url])

  if (failed)
    return (
      <div className="py-16 text-center text-sm text-gray-500">
        Не удалось показать предпросмотр — скачайте файл, чтобы открыть.
      </div>
    )
  if (!rows) return <div className="py-16 text-center text-sm text-gray-500">Загрузка…</div>
  if (rows.length === 0)
    return <div className="py-16 text-center text-sm text-gray-500">Лист пуст.</div>

  return (
    <div className="overflow-auto rounded border border-gray-200 bg-white">
      <table className="min-w-full border-collapse text-xs">
        <tbody>
          {rows.map((r, ri) => (
            <tr key={ri} className={ri === 0 ? 'bg-gray-100 font-medium' : 'odd:bg-gray-50/40'}>
              {r.map((c, ci) => (
                <td key={ci} className="whitespace-nowrap border border-gray-200 px-2 py-1">
                  {c}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {truncated && (
        <div className="px-2 py-1 text-xs text-gray-400">
          Показаны первые {PREVIEW_ROWS} строк — скачайте файл, чтобы увидеть всё.
        </div>
      )}
    </div>
  )
}

// Значок документа по типу файла: миниатюра для картинок, цветной бейдж
// расширения для pdf/word/excel/прочих.
function FileGlyph({
  name,
  mimeType,
  url,
}: {
  name: string
  mimeType: string | null
  url: string
}) {
  const ext = (name.split('.').pop() ?? '').toLowerCase()
  const isImage =
    (mimeType ?? '').startsWith('image/') || ['jpg', 'jpeg', 'png', 'webp', 'gif'].includes(ext)
  if (isImage) {
    return (
      <img src={url} alt="" className="h-10 w-10 shrink-0 rounded object-cover" loading="lazy" />
    )
  }
  const badge =
    ext === 'pdf'
      ? { text: 'PDF', cls: 'bg-red-100 text-red-700' }
      : ['doc', 'docx', 'rtf'].includes(ext)
        ? { text: 'DOC', cls: 'bg-blue-100 text-blue-700' }
        : ['ppt', 'pptx'].includes(ext)
          ? { text: 'PPT', cls: 'bg-orange-100 text-orange-700' }
          : ['xls', 'xlsx', 'csv'].includes(ext)
            ? { text: 'XLS', cls: 'bg-green-100 text-green-700' }
            : { text: (ext || 'FILE').slice(0, 4).toUpperCase(), cls: 'bg-gray-100 text-gray-600' }
  return (
    <span
      className={`flex h-10 w-10 shrink-0 items-center justify-center rounded text-[10px] font-bold ${badge.cls}`}
    >
      {badge.text}
    </span>
  )
}
